import { EVENT_TYPES } from "nemuru-components";
import React, { useContext, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { v4 as uuidv4 } from "uuid";
import {
  LENDER_APPROVAL_STATUS,
  LOAN_APPLICATION_STATE,
} from "../../../../../../config/config";
import { ProcessContext } from "../../../../../../contexts/ProcessContext";
import { useActionCallback } from "../../../../../../utils/hooks/useActionCallback";
import { useEventDispatcher } from "../../../../../../utils/hooks/useEventDispatcher";
import Text from "../../../../../shared/components/Text";
import {
  ANIMATIONS,
  ONBOARDING_PARTICIPANT_ROLES,
} from "../../../../../shared/config";
import { MILESTONES } from "../../../../../shared/sharedKernel/EventCatcher/config";
import { getLoanTimer } from "../../config";
import { BORROWER_SELECTION_STEP_NAMES } from "../../BorrowerSelectionLoanSummary/BorrowerSelectionLoanSummary";
import { areAllLoanApplicationsProcessed } from "../utils/areAllApplicationsProcessed";

export const useLoanApplication = ({
  submitOptInsConsents,
  optInsArray,
  areOptInsSynced,
  patchSetLoanCalculator,
  setLenderSelectionStep,
}) => {
  const {
    state: { loan, lenderIdsByMerchantId, participant, loanApplications },
    dispatch,
  } = useContext(ProcessContext);

  const {
    GET_LOAN_CASH_IN_CASH_OUT_METHOD_TIMEOUT_MILLISECONDS,
    GET_LOAN_SELECTED_LOAN_APPLICATION_TIMEOUT_MILLISECONDS,
    GET_LOAN_SELECTED_LOAN_APPLICATION_MAX_TIMEOUT_MILLISECONDS,
  } = getLoanTimer;

  const { dispatchEvent } = useEventDispatcher();

  const form = useForm();
  const { getValues } = form;

  const [currentTimer, setCurrentTimer] = useState(0);

  const [loanApplicationState, setLoanApplicationState] = useState({
    isLoanSubmitted: false,
    areLoanApplicationsSubmitted: false,
    showOptinsDialog: false,
  });

  const product = loan.loan_conditions.product;

  const { isLoanSubmitted, areLoanApplicationsSubmitted, showOptinsDialog } =
    loanApplicationState;

  const loanId = loan.id;

  useEffect(() => {
    if (isLoanSubmitted && !areLoanApplicationsSubmitted) {
      if (
        !loan.borrower_cash_in_cash_out_method_id &&
        participant.role === ONBOARDING_PARTICIPANT_ROLES.BORROWER
      ) {
        onGetLoanCheckCashInCashOutMethod();
      } else {
        getLenderIdsByMerchantId();
      }
    }
  }, [loan]);

  useEffect(() => {
    if (!isLoanSubmitted) return;

    if (setLenderSelectionStep) {
      handleBorrowerSelection();
    } else {
      onGetLoanWithTimerLenderStatusResponse();
    }
  }, [currentTimer]);

  useActionCallback(
    "REQUEST_PATCH_OPTIN_BY_ID_CONDITIONS_CHECK_IN_AND_SEQUENCE_FULFILLED",
    () => {
      setLoanApplicationState((s) => ({
        ...s,
        isLoanSubmitted: true,
        showOptinsDialog: false,
      }));
      submitRequestLoan(product);
    }
  );

  useActionCallback("REQUEST_PATCH_SET_LOAN_CALCULATOR_FULFILLED", () =>
    onGetLoanCheckCashInCashOutMethod()
  );

  useActionCallback("REQUEST_GET_LENDER_IDS_BY_MERCHANT_ID_FULFILLED", () => {
    const loanAppIds = [];
    for (let i = 0; i < lenderIdsByMerchantId.length; i += 1) {
      loanAppIds.push(uuidv4());
    }
    const postLoanAppData = {
      ids: loanAppIds,
      loan_id: loanId,
    };
    dispatch({
      type: "REQUEST_POST_LOAN_APPLICATIONS",
      payload: postLoanAppData,
    });
    setLoanApplicationState((s) => ({
      ...s,
      areLoanApplicationsSubmitted: true,
    }));
  });

  useActionCallback("REQUEST_POST_LOAN_APPLICATIONS_FULFILLED", () => {
    onGetLoanWithTimerLenderStatusResponse();
  });

  useActionCallback("REQUEST_POST_LOAN_APPLICATIONS_REJECTED", () => {
    dispatch({
      type: "SET_FLOW_ITEM_OK",
    });
  });

  useActionCallback(
    "REQUEST_PATCH_SYNC_BORROWER_DOCUMENTATION_BY_LOAN_ID_FULFILLED",
    () => {
      dispatch({
        type: "SET_FLOW_ITEM_OK",
      });
    }
  );

  useActionCallback(
    "REQUEST_PATCH_SYNC_BORROWER_DOCUMENTATION_BY_LOAN_ID_REJECTED",
    () => {
      dispatch({
        type: "SET_FLOW_ITEM_OK",
      });
    }
  );

  const getLenderIdsByMerchantId = () => {
    dispatch({
      type: "REQUEST_GET_LENDER_IDS_BY_MERCHANT_ID",
      payload: loan.merchant_id,
    });
  };

  const onGetLoanCheckCashInCashOutMethod = () => {
    setTimeout(() => {
      dispatch({
        type: "REQUEST_GET_LOAN",
        payload: loanId,
      });
    }, GET_LOAN_CASH_IN_CASH_OUT_METHOD_TIMEOUT_MILLISECONDS);
  };

  const submitRequestLoan = () => {
    dispatchEvent({
      name: MILESTONES.APPLICATION_REQUESTED,
      type: EVENT_TYPES.milestoneCompleted,
    });
    let patchLoanCalculatorNeeded = false;
    if (patchSetLoanCalculator)
      patchLoanCalculatorNeeded = patchSetLoanCalculator();
    if (patchLoanCalculatorNeeded === false)
      onGetLoanCheckCashInCashOutMethod();
  };

  const handleBorrowerSelection = () => {
    const isTimeExpired =
      currentTimer >
      GET_LOAN_SELECTED_LOAN_APPLICATION_MAX_TIMEOUT_MILLISECONDS;

    if (
      loanApplications &&
      loanApplications.some(
        (loanApplication) =>
          loanApplication.lender_fields.loan_application_status.state ===
          LOAN_APPLICATION_STATE.SELECTED
      )
    ) {
      dispatch({
        type: "SET_FLOW_ITEM_OK",
      });
      return;
    }

    if (areAllLoanApplicationsProcessed(loanApplications)) {
      setLenderSelectionStep(BORROWER_SELECTION_STEP_NAMES.LENDER_SELECTION);
      dispatch({
        type: "SET_FLOW_LOADING",
        payload: {
          show: false,
        },
      });
      return;
    }

    if (isTimeExpired) {
      setApprovalStatus(LENDER_APPROVAL_STATUS.MAYBE);
      dispatch({
        type: "SET_FLOW_ITEM_OK",
      });
    }

    if (!isTimeExpired) {
      setTimeout(() => {
        if (loan.loan_application_ids && loan.loan_application_ids.length > 0) {
          dispatch({
            type: "REQUEST_GET_LOAN_APPLICATIONS",
            payload: { loanId: loan.id },
          });
        } else {
          dispatch({
            type: "REQUEST_GET_LOAN",
            payload: loanId,
          });
        }
        setCurrentTimer(
          currentTimer + GET_LOAN_SELECTED_LOAN_APPLICATION_TIMEOUT_MILLISECONDS
        );
      }, GET_LOAN_SELECTED_LOAN_APPLICATION_TIMEOUT_MILLISECONDS);
    }
  };

  const onGetLoanWithTimerLenderStatusResponse = () => {
    const isTimeExpired =
      currentTimer >
      GET_LOAN_SELECTED_LOAN_APPLICATION_MAX_TIMEOUT_MILLISECONDS;

    const lenderApprovalStatus =
      loan.loan_application_selected?.lender_fields?.loan_application_status
        ?.lender_approval_status;

    if (!isTimeExpired && !lenderApprovalStatus) {
      setTimeout(() => {
        dispatch({
          type: "REQUEST_GET_LOAN",
          payload: loanId,
        });
        setCurrentTimer(
          currentTimer + GET_LOAN_SELECTED_LOAN_APPLICATION_TIMEOUT_MILLISECONDS
        );
      }, GET_LOAN_SELECTED_LOAN_APPLICATION_TIMEOUT_MILLISECONDS);
    }

    if (isTimeExpired) {
      setApprovalStatus(LENDER_APPROVAL_STATUS.MAYBE);
      dispatch({
        type: "SET_FLOW_ITEM_OK",
      });
    }

    if (
      lenderApprovalStatus === LENDER_APPROVAL_STATUS.YES ||
      lenderApprovalStatus === LENDER_APPROVAL_STATUS.MAYBE
    ) {
      setApprovalStatus(lenderApprovalStatus);
      dispatch({
        type: "REQUEST_PATCH_SYNC_BORROWER_DOCUMENTATION_BY_LOAN_ID",
        payload: {
          op: "processPatchSyncBorrowerDocumentationByLoanId",
          id: loanId,
        },
      });
    }

    if (lenderApprovalStatus === LENDER_APPROVAL_STATUS.NO) {
      setApprovalStatus(lenderApprovalStatus);
      dispatch({
        type: "SET_FLOW_ITEM_OK",
      });
    }
  };

  const setApprovalStatus = (lenderApprovalStatus) => {
    dispatch({
      type: "UPDATE_PROCESS_CONTEXT",
      payload: {
        hydratedLenderApprovalStatus: lenderApprovalStatus,
      },
    });
  };

  const dispatchPleaseWaitFlowLoading = () => {
    dispatch({
      type: "SET_FLOW_LOADING",
      payload: {
        show: true,
        animationId: ANIMATIONS.WAITING_LOAN_RESPONSE,
        loadingMessage: <Text tId="PLEASE_WAIT_APPLICATION" />,
      },
    });
  };

  const setShowOptinsDialog = (showOptinsValue) => {
    setLoanApplicationState((s) => ({
      ...s,
      showOptinsDialog: showOptinsValue,
    }));
  };

  const handleRequestLoanOrShowOptinsDialog = () => {
    if (optInsArray.length === 0 && areOptInsSynced) {
      dispatchPleaseWaitFlowLoading();
      submitOptInsConsents();
      setShowOptinsDialog(false);
    } else
      setLoanApplicationState((s) => ({
        ...s,
        showOptinsDialog: true,
      }));
  };

  const acceptTerms = () => {
    dispatchPleaseWaitFlowLoading();
    submitOptInsConsents(getValues());
    setShowOptinsDialog(false);
  };

  return {
    handleRequestLoanOrShowOptinsDialog,
    acceptTerms,
    getValues,
    form,
    showOptinsDialog,
    setShowOptinsDialog,
  };
};
